import Link from 'next/link'
import { ArrowLeft, Home, RefreshCw, Search, TrendingUp } from 'lucide-react'

interface AppErrorStateProps {
  eyebrow?: string
  title: string
  message: string
  status?: string
  primaryLabel?: string
  primaryHref?: string
  secondaryLabel?: string
  secondaryHref?: string
  onReset?: () => void
}

export function AppErrorState({
  eyebrow = 'Westline',
  title,
  message,
  status,
  primaryLabel = 'Go home',
  primaryHref = '/crm',
  secondaryLabel = 'Back to CRM',
  secondaryHref = '/crm',
  onReset,
}: AppErrorStateProps) {
  return (
    <div className="flex min-h-full items-center justify-center bg-notion-bg px-4 py-10 text-notion-text">
      <section
        aria-label="Westline recovery panel"
        className="w-full max-w-3xl overflow-hidden rounded-lg border border-notion-border bg-white shadow-[0_18px_44px_rgba(15,23,42,0.08)] dark:bg-[#171717]"
      >
        <div className="grid gap-0 md:grid-cols-[1fr_260px]">
          <div className="p-6 md:p-7">
            <div className="inline-flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-200">
              <TrendingUp className="size-3.5" />
              {eyebrow}
              {status ? ` / ${status}` : ''}
            </div>
            <h1 className="mt-4 text-2xl font-semibold leading-tight text-notion-text md:text-3xl">{title}</h1>
            <p className="mt-3 max-w-xl text-sm leading-6 text-notion-text-secondary">{message}</p>
            <div className="mt-6 flex flex-wrap items-center gap-2">
              {onReset ? (
                <button
                  type="button"
                  onClick={onReset}
                  className="inline-flex h-9 items-center gap-2 rounded-md bg-emerald-500 px-3 text-sm font-semibold text-[#171717] transition-colors hover:bg-emerald-400"
                >
                  <RefreshCw className="size-4" />
                  Try again
                </button>
              ) : (
                <Link
                  href={primaryHref}
                  className="inline-flex h-9 items-center gap-2 rounded-md bg-emerald-500 px-3 text-sm font-semibold text-[#171717] transition-colors hover:bg-emerald-400"
                >
                  <Search className="size-4" />
                  {primaryLabel}
                </Link>
              )}
              <Link
                href={secondaryHref}
                className="inline-flex h-9 items-center gap-2 rounded-md border border-notion-border bg-white px-3 text-sm font-medium text-notion-text transition-colors hover:bg-notion-hover dark:bg-transparent"
              >
                {onReset ? <Home className="size-4" /> : <ArrowLeft className="size-4" />}
                {secondaryLabel}
              </Link>
            </div>
          </div>

          <div className="border-t border-notion-border bg-notion-sidebar p-5 dark:bg-[#1f1f1f] md:border-l md:border-t-0">
            <div className="rounded-md border border-notion-border bg-white p-3 text-xs text-notion-text-secondary dark:bg-transparent">
              <div className="mb-3 flex items-center justify-between">
                <span className="font-semibold text-notion-text">Route monitor</span>
                <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-200">
                  recalculating
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="size-2 rounded-full bg-emerald-500" />
                  <span>CRM shell online</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="size-2 rounded-full bg-emerald-500" />
                  <span>Navigation ready</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="size-2 rounded-full bg-notion-text-muted" />
                  <span>Record trail missing</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
